/**
 * MessageReportList Component
 * Displays and manages reported messages for administrative review
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Badge,
  Button,
  Modal,
  Form,
  Alert,
  Spinner,
  Pagination,
  Toast,
  ToastContainer
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFlag,
  faEye,
  faTrash,
  faBan,
  faCheck,
  faExclamationTriangle,
  faBell
} from '@fortawesome/free-solid-svg-icons';
import UserAvatar from './UserAvatar';
import { formatDistanceToNow } from '../../utils/dateUtils';
import {
  getReportedMessages,
  executeModerationAction,
  dismissReport
} from '../../services/messagingService';
import './MessageReportList.css';

/**
 * MessageReportList - Display and manage reported messages
 * @param {Object} props - Component props
 * @param {Object} props.user - Current admin user
 * @returns {JSX.Element} MessageReportList component
 */
const MessageReportList = ({ user }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [actionReason, setActionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');

  /**
   * Load reported messages
   */
  const loadReports = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await getReportedMessages({ 
        page: currentPage, 
        status: filterStatus !== 'all' ? filterStatus : undefined 
      });
      
      setReports(data.reports || []);
      setTotalPages(data.pagination?.totalPages || 1);
    } catch (err) {
      console.error('Error loading reports:', err);
      setError(err.message || 'Failed to load reported messages');
      
      // Fallback to mock data for development
      const mockReports = [
        {
          reportId: 'report-1',
          messageId: 'msg-123',
          message: {
            content: 'This is an inappropriate message that was reported',
            senderId: 'user-456',
            senderName: 'John Doe',
            senderPhotoUrl: null,
            threadId: 'thread-789',
            createdAt: new Date(Date.now() - 3600000).toISOString()
          },
          reportedBy: {
            userId: 'user-789',
            displayName: 'Jane Smith',
            photoUrl: null
          },
          reason: 'Inappropriate content',
          description: 'This message contains offensive language',
          status: 'pending',
          flaggedBySystem: true,
          createdAt: new Date(Date.now() - 1800000).toISOString(),
          reviewedAt: null,
          reviewedBy: null,
          action: null
        },
        {
          reportId: 'report-2',
          messageId: 'msg-456',
          message: {
            content: 'Another reported message with spam content',
            senderId: 'user-321',
            senderName: 'Bob Johnson',
            senderPhotoUrl: null,
            threadId: 'thread-456',
            createdAt: new Date(Date.now() - 7200000).toISOString()
          },
          reportedBy: {
            userId: 'user-654',
            displayName: 'Alice Brown',
            photoUrl: null
          },
          reason: 'Spam',
          description: 'Unsolicited promotional content',
          status: 'pending',
          flaggedBySystem: false,
          createdAt: new Date(Date.now() - 3600000).toISOString(),
          reviewedAt: null,
          reviewedBy: null,
          action: null
        }
      ];

      setReports(mockReports);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, [currentPage, filterStatus]);

  /**
   * Handle viewing report details
   */
  const handleViewDetails = (report) => {
    setSelectedReport(report);
    setShowDetailModal(true);
  };

  /**
   * Handle initiating moderation action
   */
  const handleInitiateAction = (report, action) => {
    setSelectedReport(report);
    setActionType(action);
    setActionReason('');
    setShowActionModal(true);
  };

  /**
   * Handle executing moderation action
   */
  const handleExecuteAction = async () => {
    if (!selectedReport || !actionType) return;

    try {
      setProcessing(true);
      setError(null);

      await executeModerationAction({
        reportId: selectedReport.reportId,
        messageId: selectedReport.messageId,
        action: actionType,
        reason: actionReason
      });

      // Update local state
      setReports(reports.map(r =>
        r.reportId === selectedReport.reportId
          ? { ...r, status: 'resolved', action: actionType }
          : r
      ));

      // Show success notification
      const actionLabel = actionType === 'remove' ? 'removed' : 'restricted';
      setNotificationMessage(`Action completed: Message ${actionLabel} successfully`);
      setShowNotification(true);

      setShowActionModal(false);
      setSelectedReport(null);
      setActionType(null);
      setActionReason('');
    } catch (err) {
      console.error('Error executing action:', err);
      setError(err.message || 'Failed to execute moderation action');
    } finally {
      setProcessing(false);
    }
  };

  /**
   * Handle dismissing a report
   */
  const handleDismissReport = async (report) => {
    try {
      setProcessing(true);
      setError(null);

      await dismissReport(report.reportId);

      // Update local state
      setReports(reports.map(r =>
        r.reportId === report.reportId
          ? { ...r, status: 'dismissed' }
          : r
      ));

      // Show success notification
      setNotificationMessage('Report dismissed successfully');
      setShowNotification(true);
    } catch (err) {
      console.error('Error dismissing report:', err);
      setError(err.message || 'Failed to dismiss report');
    } finally {
      setProcessing(false);
    }
  };

  /**
   * Get status badge variant
   */
  const getStatusBadge = (status) => {
    const variants = {
      pending: 'warning',
      resolved: 'success',
      dismissed: 'secondary'
    };
    return variants[status] || 'secondary';
  };

  /**
   * Get action icon
   */
  const getActionIcon = (action) => {
    switch (action) {
      case 'remove':
        return faTrash;
      case 'restrict':
        return faBan;
      case 'dismiss':
        return faCheck;
      default:
        return faFlag;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Loading reported messages...</p>
      </div>
    );
  }

  return (
    <div className="message-report-list">
      {/* Success Notification Toast */}
      <ToastContainer position="top-end" className="p-3">
        <Toast 
          show={showNotification} 
          onClose={() => setShowNotification(false)}
          delay={5000}
          autohide
          bg="success"
        >
          <Toast.Header>
            <FontAwesomeIcon icon={faBell} className="me-2" />
            <strong className="me-auto">Success</strong>
          </Toast.Header>
          <Toast.Body className="text-white">
            {notificationMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="report-list-header">
        <h3>
          <FontAwesomeIcon icon={faFlag} className="me-2" />
          Reported Messages
        </h3>
        <Form.Select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="status-filter"
        >
          <option value="pending">Pending Review</option>
          <option value="resolved">Resolved</option>
          <option value="dismissed">Dismissed</option>
          <option value="all">All Reports</option>
        </Form.Select>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {reports.length === 0 ? (
        <Card className="text-center py-5">
          <Card.Body>
            <FontAwesomeIcon icon={faCheck} size="3x" className="text-success mb-3" />
            <h5>No Reports Found</h5>
            <p className="text-muted">
              {filterStatus === 'pending'
                ? 'There are no pending reports to review'
                : `No ${filterStatus} reports found`}
            </p>
          </Card.Body>
        </Card>
      ) : (
        <>
          <Table responsive hover className="report-table">
            <thead>
              <tr>
                <th>Message</th>
                <th>Sender</th>
                <th>Reported By</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => (
                <tr key={report.reportId}>
                  <td className="message-preview">
                    {report.flaggedBySystem && (
                      <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        className="text-warning me-2"
                        title="Flagged by system"
                      />
                    )}
                    <span className="message-content">
                      {report.message.content.substring(0, 50)}
                      {report.message.content.length > 50 && '...'}
                    </span>
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      <UserAvatar
                        user={{
                          displayName: report.message.senderName,
                          photoUrl: report.message.senderPhotoUrl
                        }}
                        size="sm"
                      />
                      <span className="ms-2">{report.message.senderName}</span>
                    </div>
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      <UserAvatar
                        user={{
                          displayName: report.reportedBy.displayName,
                          photoUrl: report.reportedBy.photoUrl
                        }}
                        size="sm"
                      />
                      <span className="ms-2">{report.reportedBy.displayName}</span>
                    </div>
                  </td>
                  <td>
                    <Badge bg="secondary">{report.reason}</Badge>
                  </td>
                  <td>
                    <Badge bg={getStatusBadge(report.status)}>
                      {report.status}
                    </Badge>
                  </td>
                  <td className="text-muted">
                    {formatDistanceToNow(report.createdAt)}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleViewDetails(report)}
                        title="View details"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </Button>
                      {report.status === 'pending' && (
                        <>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleInitiateAction(report, 'remove')}
                            title="Remove message"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                          <Button
                            variant="outline-warning"
                            size="sm"
                            onClick={() => handleInitiateAction(report, 'restrict')}
                            title="Restrict user"
                          >
                            <FontAwesomeIcon icon={faBan} />
                          </Button>
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => handleDismissReport(report)}
                            title="Dismiss report"
                            disabled={processing}
                          >
                            <FontAwesomeIcon icon={faCheck} />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-3">
              <Pagination>
                <Pagination.Prev
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                />
                {[...Array(totalPages)].map((_, i) => (
                  <Pagination.Item
                    key={i + 1}
                    active={i + 1 === currentPage}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                />
              </Pagination>
            </div>
          )}
        </>
      )}

      {/* Report Detail Modal */}
      <Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Report Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedReport && (
            <div className="report-details">
              <div className="detail-section">
                <h6>Message Content</h6>
                <Card className="message-card">
                  <Card.Body>{selectedReport.message.content}</Card.Body>
                </Card>
              </div>

              <div className="detail-section">
                <h6>Report Information</h6>
                <dl className="row">
                  <dt className="col-sm-4">Reason:</dt>
                  <dd className="col-sm-8">
                    <Badge bg="secondary">{selectedReport.reason}</Badge>
                  </dd>

                  <dt className="col-sm-4">Description:</dt>
                  <dd className="col-sm-8">{selectedReport.description}</dd>

                  <dt className="col-sm-4">Reported By:</dt>
                  <dd className="col-sm-8">
                    <div className="d-flex align-items-center">
                      <UserAvatar
                        user={{
                          displayName: selectedReport.reportedBy.displayName,
                          photoUrl: selectedReport.reportedBy.photoUrl
                        }}
                        size="sm"
                      />
                      <span className="ms-2">
                        {selectedReport.reportedBy.displayName}
                      </span>
                    </div>
                  </dd>

                  <dt className="col-sm-4">Reported At:</dt>
                  <dd className="col-sm-8">
                    {new Date(selectedReport.createdAt).toLocaleString()}
                  </dd>

                  <dt className="col-sm-4">System Flagged:</dt>
                  <dd className="col-sm-8">
                    {selectedReport.flaggedBySystem ? 'Yes' : 'No'}
                  </dd>

                  <dt className="col-sm-4">Status:</dt>
                  <dd className="col-sm-8">
                    <Badge bg={getStatusBadge(selectedReport.status)}>
                      {selectedReport.status}
                    </Badge>
                  </dd>
                </dl>
              </div>

              <div className="detail-section">
                <h6>Message Sender</h6>
                <div className="d-flex align-items-center">
                  <UserAvatar
                    user={{
                      displayName: selectedReport.message.senderName,
                      photoUrl: selectedReport.message.senderPhotoUrl
                    }}
                    size="md"
                  />
                  <div className="ms-3">
                    <div className="fw-bold">{selectedReport.message.senderName}</div>
                    <div className="text-muted small">
                      Sent {formatDistanceToNow(selectedReport.message.createdAt)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Action Confirmation Modal */}
      <Modal
        show={showActionModal}
        onHide={() => setShowActionModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={getActionIcon(actionType)} className="me-2" />
            Confirm Action
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            This action will be logged and cannot be undone.
          </Alert>

          <p>
            You are about to{' '}
            <strong>
              {actionType === 'remove' ? 'remove this message' : 'restrict this user'}
            </strong>
            .
          </p>

          <Form.Group className="mb-3">
            <Form.Label>Reason for action (required)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
              placeholder="Provide a detailed reason for this moderation action..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowActionModal(false)}
            disabled={processing}
          >
            Cancel
          </Button>
          <Button
            variant={actionType === 'remove' ? 'danger' : 'warning'}
            onClick={handleExecuteAction}
            disabled={processing || !actionReason.trim()}
          >
            {processing ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Processing...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={getActionIcon(actionType)} className="me-2" />
                Confirm {actionType === 'remove' ? 'Removal' : 'Restriction'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MessageReportList;
